/**
 * State income tax (spec §9). Tiered, data-driven, honest about fidelity:
 *   - 'modeled'            : brackets/flat rate + SS rule + retirement
 *                            exclusions, computed here.
 *   - 'flat_approximation' : a labeled effective rate on taxable income.
 *   - 'none'               : no income tax.
 * A custom effective-rate override always wins so nobody is blocked.
 *
 * Watch for retirement-income exclusions with INCOME CLIFFS — several states
 * eliminate the exclusion entirely above a threshold, producing enormous
 * effective marginal rates. The cliff is modeled, and tested.
 */
import type { FilingStatus } from '../../model/types';
import type { Bracket } from '../data/federal';
import { ordinaryTaxOn } from './federal';

export interface StateTaxProfile {
  code: string;
  name: string;
  fidelity: 'modeled' | 'flat_approximation' | 'none';
  /** Progressive brackets (or a single entry for flat states). */
  brackets?: Record<FilingStatus, Bracket[]>;
  /** Flat effective rate for 'flat_approximation' states. */
  approxEffectiveRate?: number;
  standardDeduction?: Record<FilingStatus, number>;
  /** Whether the state taxes Social Security benefits (possibly partially). */
  socialSecurityTaxed: boolean;
  /** Applied to the federally-taxable portion of SS when taxed (0-1). */
  socialSecurityTaxedFraction?: number;
  /**
   * Retirement-income exclusion (pensions/IRA/401k distributions).
   * `incomeLimit` is a CLIFF: one dollar of total income above it and the
   * whole exclusion is gone.
   */
  retirementIncomeExclusion?: {
    maxAmount: Record<FilingStatus, number>;
    incomeLimit?: number;
    minAge: number;
  };
  capitalGainsTreatment: 'as_ordinary' | 'excluded';
  notes: string; // surfaced in the UI
  lastVerified: string;
}

export interface StateTaxInput {
  year: number;
  filingStatus: FilingStatus;
  /** Oldest household age (exclusion minAge tests). */
  age: number;
  /** Ordinary income before SS: wages, pensions, withdrawals, conversions... */
  ordinaryIncome: number;
  /** The subset of ordinaryIncome that is retirement income (pension + IRA/401k
   *  distributions + RMDs) for exclusion purposes. */
  retirementIncome: number;
  capitalGains: number;
  /** Federally-taxable portion of Social Security benefits. */
  federallyTaxableSS: number;
  /** User's custom effective rate — always wins when set. */
  customEffectiveRate?: number;
}

export interface StateTaxResult {
  tax: number;
  taxableIncome: number;
  /** True when the retirement exclusion was lost to an income cliff. */
  exclusionLostToCliff: boolean;
}

export function computeStateTax(profile: StateTaxProfile, input: StateTaxInput): StateTaxResult {
  if (input.customEffectiveRate != null) {
    const base = input.ordinaryIncome + input.capitalGains + input.federallyTaxableSS;
    return { tax: Math.max(0, base) * input.customEffectiveRate, taxableIncome: base, exclusionLostToCliff: false };
  }

  if (profile.fidelity === 'none') {
    return { tax: 0, taxableIncome: 0, exclusionLostToCliff: false };
  }

  // Income components in the state base.
  let base = input.ordinaryIncome;
  if (profile.capitalGainsTreatment === 'as_ordinary') base += input.capitalGains;
  if (profile.socialSecurityTaxed) {
    base += input.federallyTaxableSS * (profile.socialSecurityTaxedFraction ?? 1);
  }

  // Retirement-income exclusion, with the cliff.
  let exclusionLostToCliff = false;
  const excl = profile.retirementIncomeExclusion;
  if (excl && input.age >= excl.minAge) {
    const totalIncome = input.ordinaryIncome + input.capitalGains + input.federallyTaxableSS;
    if (excl.incomeLimit != null && totalIncome > excl.incomeLimit) {
      exclusionLostToCliff = true; // one dollar over: the whole exclusion is gone
    } else {
      base -= Math.min(excl.maxAmount[input.filingStatus], Math.max(0, input.retirementIncome));
    }
  }

  if (profile.fidelity === 'flat_approximation') {
    const tax = Math.max(0, base) * (profile.approxEffectiveRate ?? 0.045);
    return { tax, taxableIncome: Math.max(0, base), exclusionLostToCliff };
  }

  // 'modeled': standard deduction then brackets.
  const deduction = profile.standardDeduction?.[input.filingStatus] ?? 0;
  const taxable = Math.max(0, base - deduction);
  const brackets = profile.brackets?.[input.filingStatus];
  const tax = brackets ? ordinaryTaxOn(taxable, brackets) : 0;
  return { tax, taxableIncome: taxable, exclusionLostToCliff };
}
